import { Check, Clock, UserCheck, X } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { ApprovalDecisionRequest, ApprovalQueueQuery, ApprovalRequest, ApprovalStatus } from '../../api/types';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, FilterBar, StatCard, StatGrid, type Column } from '../../components/blocks';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { useWrite, WriteError } from '../../components/Write';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { elapsedSeconds, formatDateTime, formatDuration, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { FilterSelect, ListSection, PersonRef, RecordRef, RefreshButton, ResetButton, useAdminCrumbs, useCatalogue, useParamSetter } from './ui';

const STATUSES: ApprovalStatus[] = ['PENDING', 'APPROVED', 'REJECTED', 'EXPIRED', 'EXECUTED', 'CANCELLED'];

const STATUS_TONE: Record<ApprovalStatus, Tone> = {
  PENDING: 'pending',
  APPROVED: 'ok',
  REJECTED: 'serious',
  EXPIRED: 'neutral',
  EXECUTED: 'ok',
  CANCELLED: 'neutral',
};

const SOON_HOURS = 4;

/** `/admin/approvals`: the changes waiting for a second operator. */
export function ApprovalsPage() {
  const { t } = useTranslation('admin');
  useDocumentTitle(t('approvals.title'));
  const crumbs = useAdminCrumbs(t('approvals.title'));
  const [params, set, reset] = useParamSetter();
  const { permissionName } = useCatalogue();
  const status = params.get('status') || '';
  const q: ApprovalQueueQuery = {
    status: (status || undefined) as ApprovalQueueQuery['status'],
    before: params.get('before') || undefined,
    limit: 50,
  };
  const result = useApi((signal) => api.listApprovalQueue(q, { signal }), [params.toString()]);
  const rows = result.status === 'success' ? result.data.items : [];
  const waiting = rows.filter((a) => a.status === 'PENDING');
  // The open request is addressable, so a colleague can be sent straight to it and the staff
  // page can link to the request somebody is waiting on.
  const opened = params.get('request');
  const open = opened ? (rows.find((a) => a.id === opened) ?? null) : null;
  const setOpen = (a: ApprovalRequest | null) => set('request', a?.id ?? '');

  const columns: Column<ApprovalRequest>[] = [
    {
      key: 'change',
      header: t('approvals.col.change'),
      className: 'whitespace-normal',
      cell: (a) => (
        <span>
          <span className="font-medium">{permissionName(a.action, a.action_description)}</span>
          <span className="block text-xs font-normal text-muted-foreground">{a.action}</span>
        </span>
      ),
    },
    { key: 'resource', header: t('approvals.col.resource'), cell: (a) => <RecordRef resourceType={a.resource_type} resourceId={a.resource_id} resourceName={a.resource_name} /> },
    {
      key: 'requester',
      header: t('approvals.col.requester'),
      className: 'whitespace-normal',
      cell: (a) => (
        // `space-y` needs block children; the name and the badge are both inline, so a long
        // e-mail address slides under the badge instead of pushing it down.
        <span className="flex flex-col items-start gap-1">
          <PersonRef id={a.requested_by} name={a.requested_by_name} email={a.requested_by_email} automatedLabel={t('audit.automated')} />
          {a.requested_by_self ? (
            <Badge tone="warn" title={t('note.selfBlocked')}>
              {t('approvals.self')}
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'age',
      header: t('approvals.col.age'),
      className: 'w-40 whitespace-normal',
      cell: (a) => (
        <span title={formatDateTime(a.requested_at)}>
          {formatDuration(elapsedSeconds(a.requested_at))}
          {a.status === 'PENDING' ? <span className="block text-xs text-muted-foreground">{t('approvals.expiresIn', { when: formatRelative(a.expires_at) })}</span> : null}
        </span>
      ),
    },
    { key: 'status', header: t('approvals.col.status'), cell: (a) => <Badge tone={STATUS_TONE[a.status]}>{t(`approvalStatus.${a.status}`)}</Badge> },
    {
      key: 'decision',
      header: t('approvals.col.decision'),
      className: 'whitespace-normal',
      cell: (a) =>
        a.decided_by ? (
          <span className="text-sm">
            <PersonRef id={a.decided_by} name={a.decided_by_name} email={a.decided_by_email} automatedLabel={t('audit.automated')} />
          </span>
        ) : (
          <span className="text-muted-foreground">{t('approvals.undecided')}</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader title={t('approvals.title')} crumbs={crumbs} subtitle={t('approvals.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <div className="space-y-4">
        <Callout tone="info" title={t('note.fourEyesTitle')}>
          {t('note.fourEyesBody')} {t('note.audited')}
        </Callout>
        {result.status === 'success' ? (
          <StatGrid>
            <StatCard label={t('approvals.stat.waiting')} value={waiting.length} tone={waiting.length > 0 ? 'pending' : 'ok'} icon={Clock} />
            <StatCard
              label={t('approvals.stat.yours')}
              value={waiting.filter((a) => a.requested_by_self).length}
              hint={t('approvals.stat.yoursHint')}
              icon={UserCheck}
              tone={waiting.some((a) => a.requested_by_self) ? 'warn' : 'neutral'}
              status={waiting.some((a) => a.requested_by_self) ? t('approvals.self') : undefined}
            />
            <StatCard
              label={t('approvals.stat.soon')}
              value={waiting.filter((a) => elapsedSeconds(new Date().toISOString(), a.expires_at) < SOON_HOURS * 3600).length}
              hint={t('approvals.stat.soonHint')}
              tone="serious"
            />
            <StatCard label={t('approvals.stat.requesters')} value={new Set(waiting.map((a) => a.requested_by)).size} to="/admin/staff" linkLabel={t('staff.title')} />
          </StatGrid>
        ) : null}
        <FilterBar actions={<ResetButton onClick={reset} disabled={!status} />}>
          <FilterSelect
            label={t('approvals.filterStatus')}
            value={status}
            onChange={(v) => set('status', v)}
            className="min-w-44"
            // No filter is not "any state": it is the live queue, pending and not yet timed out.
            anyLabel={t('approvals.statusLive')}
            options={[{ value: 'ALL', label: t('approvals.statusAll') }, ...STATUSES.map((s) => ({ value: s, label: t(`approvalStatus.${s}`) }))]}
          />
        </FilterBar>
        <ListSection
          result={result}
          rows={rows}
          columns={columns}
          rowKey={(a) => a.id}
          onRowClick={setOpen}
          description={result.status === 'success' ? t('approvals.count', { count: rows.length }) : undefined}
          errorTitle={t('approvals.loadError')}
          empty={status ? t('approvals.emptyFiltered') : t('approvals.empty')}
          nextBefore={result.status === 'success' ? result.data.next_before : null}
          onNext={(c) => set('before', c)}
          onFirst={() => set('before', '')}
          first={!q.before}
        />
      </div>
      {open ? (
        <DecisionDialog
          request={open}
          onClose={() => setOpen(null)}
          onDecided={() => {
            setOpen(null);
            result.reload();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The whole request, and — for a request somebody else raised — the decision. Agreeing or
 * refusing asks for confirmation first, and always records the reason with the decision.
 */
function DecisionDialog({ request: a, onClose, onDecided }: { request: ApprovalRequest; onClose: () => void; onDecided: () => void }) {
  const { t } = useTranslation('admin');
  const { permissionName } = useCatalogue();
  const id = useId();
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState<'approve' | 'reject' | null>(null);
  const write = useWrite((req: ApprovalDecisionRequest, opts) => api.decideApproval(a.id, req, opts));
  const decidable = a.status === 'PENDING' && !a.requested_by_self;

  const submit = async () => {
    if (!reason.trim() || !confirm) return;
    if (await write.run({ approve: confirm === 'approve', reason: reason.trim() })) onDecided();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !write.busy && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('approvals.dialog.title')}</DialogTitle>
          <DialogDescription>{permissionName(a.action, a.action_description)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <KeyValue
            columns={2}
            rows={[
              [t('approvals.dialog.change'), <code key="a" className="text-xs">{a.action}</code>],
              [t('approvals.dialog.resource'), <RecordRef key="r" resourceType={a.resource_type} resourceId={a.resource_id} resourceName={a.resource_name} />],
              [
                t('approvals.dialog.requester'),
                <PersonRef key="p" id={a.requested_by} name={a.requested_by_name} email={a.requested_by_email} automatedLabel={t('audit.automated')} />,
              ],
              [t('approvals.dialog.requested'), formatDateTime(a.requested_at)],
              [t('approvals.dialog.status'), <Badge key="s" tone={STATUS_TONE[a.status]}>{t(`approvalStatus.${a.status}`)}</Badge>],
              [t('approvals.dialog.expires'), formatDateTime(a.expires_at)],
            ]}
          />
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">{t('approvals.dialog.reason')}</div>
            <p className="rounded-md border bg-muted/40 p-3 text-sm">{a.reason}</p>
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">{t('approvals.dialog.details')}</div>
            <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs">{JSON.stringify(a.payload, null, 2)}</pre>
          </div>
          {a.decided_by ? (
            <KeyValue
              columns={2}
              rows={[
                [t('approvals.dialog.decidedBy'), <PersonRef key="d" id={a.decided_by} name={a.decided_by_name} email={a.decided_by_email} automatedLabel={t('audit.automated')} />],
                [t('approvals.dialog.decidedAt'), a.decided_at ? formatDateTime(a.decided_at) : '—'],
                [t('approvals.dialog.decisionReason'), a.decision_reason],
              ]}
            />
          ) : null}

          {a.requested_by_self && a.status === 'PENDING' ? <Callout tone="warn">{t('note.selfBlocked')}</Callout> : null}

          {decidable ? (
            <div className="space-y-2 rounded-lg border p-3">
              <label htmlFor={`${id}-reason`} className="text-xs font-medium text-muted-foreground">
                {t('approvals.dialog.yourReason')}
              </label>
              <Textarea
                id={`${id}-reason`}
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('approvals.dialog.yourReasonPlaceholder')}
                disabled={write.busy}
              />
              <p className="text-xs text-muted-foreground">{t('approvals.dialog.reasonRequired')}</p>
              {confirm ? <Callout tone={confirm === 'approve' ? 'warn' : 'info'}>{confirm === 'approve' ? t('approvals.dialog.confirmApprove') : t('approvals.dialog.confirmReject')}</Callout> : null}
            </div>
          ) : null}
          {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={write.busy}>
            {t('approvals.dialog.close')}
          </Button>
          {decidable ? (
            confirm ? (
              <Button type="button" variant={confirm === 'approve' ? 'default' : 'destructive'} onClick={submit} disabled={write.busy || !reason.trim()}>
                {confirm === 'approve' ? <Check /> : <X />}
                {write.busy ? t('approvals.dialog.working') : confirm === 'approve' ? t('approvals.dialog.approve') : t('approvals.dialog.reject')}
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => setConfirm('reject')} disabled={!reason.trim()}>
                  <X /> {t('approvals.dialog.reject')}
                </Button>
                <Button type="button" onClick={() => setConfirm('approve')} disabled={!reason.trim()}>
                  <Check /> {t('approvals.dialog.approve')}
                </Button>
              </>
            )
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
